import React, { useMemo, useRef, useState } from 'react';
import { FiMic, FiFileText, FiLink, FiUploadCloud, FiFolder, FiX } from 'react-icons/fi';
import Button, { ImportMaterialButton as ImportButton } from '../../ui/buttons/Button';
import StyledActionCard from './StyledActionCard';
import { isAudioFile } from '../../../services/extractors/indexExtractor';
import { isPdfFile } from '../../../services/extractors/pdfExtractor';
import { useColorContext } from '../../../context/colorContext';
import { useLanguageContext } from '../../../context/languageContext';

// ─── Keyframe animations (injected once into the document) ───────────────────
const ANIM_STYLE_ID = 'plynote-menu-anim';
if (typeof document !== 'undefined' && !document.getElementById(ANIM_STYLE_ID)) {
	const el = document.createElement('style');
	el.id = ANIM_STYLE_ID;
	el.textContent = `
		@keyframes menuIn  { from { opacity:0; transform:scale(.95) translateY(-12px); } to { opacity:1; transform:scale(1) translateY(0); } }
		@keyframes menuOut { from { opacity:1; transform:scale(1) translateY(0); } to { opacity:0; transform:scale(.95) translateY(-8px); } }
		@keyframes bdIn    { from { opacity:0; } to { opacity:1; } }
		@keyframes bdOut   { from { opacity:1; } to { opacity:0; } }
	`;
	document.head.appendChild(el);
}

const toSafeHex = (hex, fallback = '#1976d2') => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#1976d2') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const useButtonsImportColors = () => {
	const {
		bgPrimary,
		bgSecondary,
		textPrimary,
		textSecondary,
		activeAccent,
		functionalColors,
	} = useColorContext();

	const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0A');
	const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
	const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
	const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
	const safeAccent = toSafeHex(activeAccent, '#1976d2');
	const safeInfo = toSafeHex(functionalColors?.info, '#3B82F6');
	const safeError = toSafeHex(functionalColors?.error, '#EF4444');

	return useMemo(() => ({
		firstBackground: safeBgPrimary,
		secondBackground: safeBgSecondary,
		firstVark: safeAccent,
		secondVark: safeInfo,
		textPrimary: safeTextPrimary,
		textSecondary: safeTextSecondary,
		overlay: hexToRgba(safeBgPrimary, 0.78, safeBgPrimary),
		panelBg: safeBgPrimary,
		panelGradient: `linear-gradient(180deg, ${hexToRgba(safeBgPrimary, 0.98, safeBgPrimary)} 0%, ${hexToRgba(safeBgSecondary, 0.96, safeBgSecondary)} 100%)`,
		panelBorder: hexToRgba(safeTextPrimary, 0.16, safeTextPrimary),
		panelInset: hexToRgba(safeTextPrimary, 0.06, safeTextPrimary),
		panelShadow: `0 22px 52px ${hexToRgba(safeBgPrimary, 0.58, safeBgPrimary)}`,
		panelGlow: `0 0 0 1px ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
		divider: hexToRgba(safeTextPrimary, 0.12, safeTextPrimary),
		inputBg: hexToRgba(safeBgSecondary, 0.82, safeBgSecondary),
		inputBorder: hexToRgba(safeTextPrimary, 0.22, safeTextPrimary),
		inputBorderFocus: hexToRgba(safeAccent, 0.78, safeAccent),
		inputText: safeTextPrimary,
		subtleText: safeTextSecondary,
		mutedText: safeTextSecondary,
		dangerBg: safeError,
		dangerBorder: safeError,
		dangerSurface: hexToRgba(safeError, 0.2, safeError),
		dangerText: safeTextPrimary,
		accentBgSoft: hexToRgba(safeAccent, 0.2, safeAccent),
		accentBorder: hexToRgba(safeAccent, 0.55, safeAccent),
		accentText: safeTextPrimary,
		cardBg: safeBgSecondary,
		cardBgHover: safeBgPrimary,
		cardBorder: hexToRgba(safeTextPrimary, 0.14, safeTextPrimary),
		closeButtonBg: hexToRgba(safeBgSecondary, 0.92, safeBgSecondary),
		chipBg: hexToRgba(safeBgSecondary, 0.62, safeBgSecondary),
		chipBorder: hexToRgba(safeTextPrimary, 0.18, safeTextPrimary),
		chipText: safeTextPrimary,
	}), [safeBgPrimary, safeBgSecondary, safeTextPrimary, safeTextSecondary, safeAccent, safeInfo, safeError]);
};

// ─── Shared styles ────────────────────────────────────────────────────────────
const getBackdropBase = (colors) => ({
	position: 'fixed', inset: 0,
	background: colors.overlay,
	backdropFilter: 'blur(7px)',
	display: 'flex', alignItems: 'center', justifyContent: 'center',
	zIndex: 9999, padding: '20px',
});

const getMenuBase = (colors) => ({
	background: colors.panelGradient,
	border: `1px solid ${colors.panelBorder}`,
	borderRadius: '22px',
	padding: '28px',
	display: 'flex', flexDirection: 'column', gap: '20px',
	width: 'min(660px, 96vw)',
	maxHeight: '88vh', overflowY: 'auto',
	boxShadow: `${colors.panelShadow}, ${colors.panelGlow}, inset 0 1px 0 ${colors.panelInset}`,
});

const buttonContentStyle = {
	display: 'inline-flex', alignItems: 'center', gap: '8px',
};

const getDividerStyle = (colors) => ({
	height: '1px',
	background: `linear-gradient(90deg, transparent 0%, ${colors.divider} 18%, ${colors.divider} 82%, transparent 100%)`,
});

const getInputBase = (colors) => ({
	width: '100%',
	padding: '12px 16px',
	borderRadius: '10px',
	border: `1px solid ${colors.inputBorder}`,
	background: colors.inputBg,
	color: colors.inputText,
	outline: 'none',
	fontSize: '14px',
	lineHeight: '1.5',
	boxSizing: 'border-box',
	boxShadow: `inset 0 1px 0 ${colors.panelInset}`,
	transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
});

const getElegantMenuButtonStyle = (colors, tone = 'primary') => {
	const toneColor = tone === 'secondary' ? colors.secondVark : colors.firstVark;
	return {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '8px',
		width: '100%',
		padding: '12px 16px',
		borderRadius: '12px',
		border: `1px solid ${hexToRgba(toneColor, 0.6, toneColor)}`,
		background: `linear-gradient(135deg, ${hexToRgba(toneColor, 0.24, toneColor)} 0%, ${hexToRgba(toneColor, 0.16, toneColor)} 100%)`,
		color: colors.textPrimary,
		fontSize: '14px',
		fontWeight: 600,
		cursor: 'pointer',
		boxShadow: `0 10px 22px ${hexToRgba(toneColor, 0.2, toneColor)}, inset 0 1px 0 ${colors.panelInset}`,
		transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease',
	};
};

const getCompactAccentButtonStyle = (colors, tone = 'primary') => {
	const toneColor = tone === 'secondary' ? colors.secondVark : colors.firstVark;
	return {
		height: 36,
		padding: '0 13px',
		borderRadius: 10,
		border: `1px solid ${hexToRgba(toneColor, 0.56, toneColor)}`,
		background: `linear-gradient(135deg, ${hexToRgba(toneColor, 0.22, toneColor)} 0%, ${hexToRgba(toneColor, 0.12, toneColor)} 100%)`,
		color: colors.textPrimary,
		fontSize: 12,
		fontWeight: 650,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
		cursor: 'pointer',
		boxShadow: `0 8px 18px ${hexToRgba(toneColor, 0.16, toneColor)}, inset 0 1px 0 ${colors.panelInset}`,
		transition: 'transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease',
	};
};

const importSizePresets = {
	sm: {
		minHeight: 56,
		padding: '10px 12px',
		mainTextSize: 13,
		secondaryTextSize: 11,
		iconSize: 16,
	},
	md: {
		minHeight: 66,
		padding: '12px 14px',
		mainTextSize: 14,
		secondaryTextSize: 12,
		iconSize: 18,
	},
	lg: {
		minHeight: 74,
		padding: '14px 16px',
		mainTextSize: 15,
		secondaryTextSize: 12,
		iconSize: 30,
	},
};

const mergeImportButtonProps = (base = {}, globalProps = {}, specificProps = {}) => {
	const merged = {
		...base,
		...globalProps,
		...specificProps,
	};

	merged.style = {
		...(base.style || {}),
		...(globalProps.style || {}),
		...(specificProps.style || {}),
	};

	merged.mainTextStyle = {
		...(base.mainTextStyle || {}),
		...(globalProps.mainTextStyle || {}),
		...(specificProps.mainTextStyle || {}),
	};

	merged.secondaryTextStyle = {
		...(base.secondaryTextStyle || {}),
		...(globalProps.secondaryTextStyle || {}),
		...(specificProps.secondaryTextStyle || {}),
	};

	merged.iconStyle = {
		...(base.iconStyle || {}),
		...(globalProps.iconStyle || {}),
		...(specificProps.iconStyle || {}),
	};

	merged.iconContainerStyle = {
		...(base.iconContainerStyle || {}),
		...(globalProps.iconContainerStyle || {}),
		...(specificProps.iconContainerStyle || {}),
	};

	return merged;
};

const toFileArray = (filesOrFile) => {
	if (!filesOrFile) return [];
	if (typeof FileList !== 'undefined' && filesOrFile instanceof FileList) {
		return Array.from(filesOrFile);
	}
	return Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
};

// ─── ActionCard ───────────────────────────────────────────────────────────────
// ActionCard replaced by StyledActionCard for unified style

// ─── CenteredMenu ─────────────────────────────────────────────────────────────
const CenteredMenu = ({ title, subtitle, onClose, children, colors }) => {
	const [closing, setClosing] = useState(false);

	const close = () => {
		setClosing(true);
		setTimeout(onClose, 190);
	};

	return (
		<div
			style={{
				...getBackdropBase(colors),
				animation: closing ? 'bdOut .19s ease forwards' : 'bdIn .22s ease forwards',
			}}
			onClick={close}
		>
			<div
				style={{
					...getMenuBase(colors),
					animation: closing
						? 'menuOut .19s cubic-bezier(.4,0,1,1) forwards'
						: 'menuIn .26s cubic-bezier(.16,1,.3,1) forwards',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '4px', position: 'relative' }}>
					<div style={{ color: colors.textPrimary, fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em' }}>
						{title}
					</div>
					{subtitle && (
						<div style={{ color: colors.mutedText, fontSize: '13px', marginTop: '2px' }}>{subtitle}</div>
					)}
					<button
						type="button"
						onClick={close}
						style={{
							position: 'absolute', top: 0, right: 0,
							background: colors.closeButtonBg,
							border: `1px solid ${colors.panelBorder}`,
							borderRadius: '8px',
							color: colors.textSecondary,
							cursor: 'pointer',
							padding: '7px',
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							boxShadow: `0 6px 16px ${hexToRgba(colors.firstBackground, 0.36, colors.firstBackground)}`,
							transition: 'background 0.20s ease, color 0.20s ease, transform 0.20s ease',
					}}
						onMouseEnter={(e) => {
							e.currentTarget.style.background = colors.accentBgSoft;
							e.currentTarget.style.color = colors.textPrimary;
							e.currentTarget.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.background = colors.closeButtonBg;
							e.currentTarget.style.color = colors.textSecondary;
							e.currentTarget.style.transform = 'translateY(0)';
						}}
					>
						<FiX size={16} />
					</button>
				</div>

				<div style={getDividerStyle(colors)} />

				{children}
			</div>
		</div>
	);
};

// ─── ImportRecordAudioFeature ─────────────────────────────────────────────────
const ImportRecordAudioFeature = ({
	onAudioAction,
	size = 'md',
	allImportButtonProps = {},
	triggerImportButtonProps = {},
	stopImportButtonProps = {},
	addImportButtonProps = {},
	resetImportButtonProps = {},
	confirmImportButtonProps = {},
}) => {
	const colors = useButtonsImportColors();
	const { t } = useLanguageContext();
	const [open, setOpen] = useState(false);
	const [audioFiles, setAudioFiles] = useState([]);
	const [dragOver, setDragOver] = useState(false);
	const [isExtractingAudio, setIsExtractingAudio] = useState(false);
	const [isRecording, setIsRecording] = useState(false);
	const [recordingSeconds, setRecordingSeconds] = useState(0);
	const [recordingError, setRecordingError] = useState('');
	const audioInputRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const mediaStreamRef = useRef(null);
	const recordingChunksRef = useRef([]);
	const timerRef = useRef(null);

	const resetRecordingTimer = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
		setRecordingSeconds(0);
	};

	const stopMediaTracks = () => {
		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((track) => track.stop());
			mediaStreamRef.current = null;
		}
	};

	const stopRecording = () => {
		const recorder = mediaRecorderRef.current;
		if (recorder && recorder.state !== 'inactive') {
			recorder.stop();
		} else {
			setIsRecording(false);
			resetRecordingTimer();
			stopMediaTracks();
		}
	};

	const startRecording = async () => {
		if (isRecording) return;
		setRecordingError('');

		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
			setRecordingError('Recording is not available on this browser.');
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaStreamRef.current = stream;

			const preferredTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
			const canCheckMime = typeof MediaRecorder.isTypeSupported === 'function';
			const selectedType = canCheckMime
				? preferredTypes.find((type) => MediaRecorder.isTypeSupported(type))
				: null;

			let recorder;
			try {
				recorder = selectedType ? new MediaRecorder(stream, { mimeType: selectedType }) : new MediaRecorder(stream);
			} catch (error) {
				recorder = new MediaRecorder(stream);
			}

			recordingChunksRef.current = [];
			recorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) {
					recordingChunksRef.current.push(event.data);
				}
			};

			recorder.onstop = () => {
				const mimeType = recorder.mimeType || selectedType || 'audio/webm';
				const extension = mimeType.includes('mp4') ? 'm4a' : 'webm';
				const blob = new Blob(recordingChunksRef.current, { type: mimeType });
				if (blob.size > 0) {
					const fileName = `recording-${Date.now()}.${extension}`;
					const file = new File([blob], fileName, { type: mimeType });
					handleAudioSelected(file);
				}

				recordingChunksRef.current = [];
				mediaRecorderRef.current = null;
				setIsRecording(false);
				resetRecordingTimer();
				stopMediaTracks();
			};

			mediaRecorderRef.current = recorder;
			recorder.start();
			setIsRecording(true);
			setRecordingSeconds(0);
			timerRef.current = setInterval(() => {
				setRecordingSeconds((prev) => prev + 1);
			}, 1000);
		} catch (error) {
			stopMediaTracks();
			setIsRecording(false);
			resetRecordingTimer();
			setRecordingError('Unable to start audio recording. Please allow microphone access and try again.');
		}
	};

	const closeAudioMenu = () => {
		if (isRecording) {
			stopRecording();
		}
		setRecordingError('');
		setOpen(false);
	};

	const recordingLabel = `${Math.floor(recordingSeconds / 60).toString().padStart(2, '0')}:${(recordingSeconds % 60).toString().padStart(2, '0')}`;

	const handleAudioSelected = (filesOrFile) => {
		const nextFiles = toFileArray(filesOrFile);
		if (nextFiles.length === 0) return;
		setAudioFiles((currentFiles) => [...currentFiles, ...nextFiles]);
	};

	const removeAudio = (index) => {
		setAudioFiles((currentFiles) => currentFiles.filter((_, i) => i !== index));
	};

	const clearAllAudio = () => {
		setAudioFiles([]);
		if (audioInputRef.current) audioInputRef.current.value = '';
	};

	const confirmAudio = async () => {
		if (audioFiles.length === 0 || isExtractingAudio) return;

		setIsExtractingAudio(true);
		try {
			const extracted = await Promise.all(audioFiles.map(async (file) => {
				if (!isAudioFile(file)) {
					return {
						type: 'audio',
						fileName: file?.name || 'Audio file',
						text: 'Unsupported format for audio import.',
						file: null,
					};
				}

				return {
					type: 'audio',
					fileName: file?.name || 'Audio file',
					mimeType: file?.type || 'audio/unknown',
					size: Number(file?.size) || 0,
					lastModified: Number(file?.lastModified) || null,
					file,
				};
			}));

			extracted.forEach((payload) => onAudioAction?.(payload));

			setAudioFiles([]);
			setOpen(false);
		} catch (error) {
			onAudioAction?.({
				type: 'audio',
				fileName: 'Audio import',
				text: `Audio import error: ${error?.message || 'unknown error'}`,
			});
		} finally {
			setIsExtractingAudio(false);
		}
	};

	const audioDropZoneStyle = useMemo(() => ({
		border: `2px dashed ${dragOver ? colors.firstVark : colors.inputBorder}`,
		borderRadius: '12px',
		padding: '28px 20px',
		textAlign: 'center',
		color: dragOver ? colors.textPrimary : colors.textSecondary,
		background: dragOver ? colors.accentBgSoft : colors.cardBg,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	}), [colors, dragOver]);

	const sizePreset = importSizePresets[size] || importSizePresets.md;

	return (
		<>
			<StyledActionCard
				icon={<FiMic size={sizePreset.iconSize} color={colors.firstVark} strokeWidth={1.5} />}
				title={t('import.audioTriggerTitle')}
				desc={t('import.audioTriggerDesc')}
				highlight={false}
				onClick={() => setOpen((v) => !v)}
				style={{ minHeight: sizePreset.minHeight, padding: sizePreset.padding, ...allImportButtonProps.style, ...triggerImportButtonProps.style }}
				{...allImportButtonProps}
				{...triggerImportButtonProps}
			/>

			{open && (
				<CenteredMenu
					title={t('import.audioTitle')}
					subtitle={t('import.audioSubtitle')}
					onClose={closeAudioMenu}
					colors={colors}
				>
					{audioFiles.length === 0 ? (
						<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							{recordingError && (
								<div style={{
									background: colors.dangerSurface,
									border: `1px solid ${colors.dangerBorder}`,
									borderRadius: '12px',
									padding: '10px 12px',
									color: colors.dangerText,
									fontSize: '13px',
									boxShadow: `0 10px 22px ${hexToRgba(colors.dangerBg, 0.18, colors.dangerBg)}`,
								}}>
									{recordingError}
								</div>
							)}
							{isRecording && (
								<div style={{
									display: 'flex', alignItems: 'center', justifyContent: 'space-between',
									background: colors.accentBgSoft,
									border: `1px solid ${colors.accentBorder}`,
									borderRadius: '12px', padding: '10px 12px',
									boxShadow: `0 10px 24px ${hexToRgba(colors.firstVark, 0.14, colors.firstVark)}`,
								}}>
									<div style={{ color: colors.accentText, fontSize: '13px', fontWeight: 600 }}>
										{t('import.recordingInProgress')} • {recordingLabel}
									</div>
									<Button variant="outline" color={colors.secondVark} size="sm" onClick={stopRecording}>
										{t('import.stopRecording')}
									</Button>
								</div>
							)}
							<div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
								<StyledActionCard
									icon={<FiMic size={32} color={colors.firstVark} strokeWidth={1.5} />}
									title={t('import.startRecording')}
									desc={t('import.startRecordingDesc')}
									onClick={startRecording}
									highlight={false}
									style={{ minWidth: 180, flex: '1 1 calc(50% - 6px)' }}
								/>
								<StyledActionCard
									icon={<FiUploadCloud size={32} color={colors.firstVark} strokeWidth={1.5} />}
									title={t('import.importAudioFiles')}
									desc={t('import.importAudioFormats')}
									onClick={() => audioInputRef.current?.click()}
									highlight={false}
									style={{ minWidth: 180, flex: '1 1 calc(50% - 6px)' }}
								/>
							</div>
							<div
								style={audioDropZoneStyle}
								onClick={() => audioInputRef.current?.click()}
								onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
								onDragLeave={() => setDragOver(false)}
								onDrop={(e) => {
									e.preventDefault();
									setDragOver(false);
									handleAudioSelected(e.dataTransfer?.files);
								}}
							>
								<div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
									<FiUploadCloud size={32} />
								</div>
								<div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>
									{t('import.dragDropAudio')}
								</div>
								<div style={{ fontSize: '12px', color: colors.textSecondary }}>{t('import.importAudioFormats')}</div>
							</div>
						</div>
					) : (
						<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
								<button
									type="button"
									onClick={() => audioInputRef.current?.click()}
									style={getCompactAccentButtonStyle(colors, 'primary')}
									onMouseEnter={(event) => {
										event.currentTarget.style.transform = 'translateY(-1px)';
										event.currentTarget.style.filter = 'brightness(1.05)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.86, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 12px 22px ${hexToRgba(colors.firstVark, 0.22, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
									onMouseLeave={(event) => {
										event.currentTarget.style.transform = 'translateY(0)';
										event.currentTarget.style.filter = 'brightness(1)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.56, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 8px 18px ${hexToRgba(colors.firstVark, 0.16, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
								>
									<span style={buttonContentStyle}><FiUploadCloud size={14} />{t('import.addAnotherAudio')}</span>
								</button>
								<button
									type="button"
									onClick={clearAllAudio}
									style={getCompactAccentButtonStyle(colors, 'primary')}
									onMouseEnter={(event) => {
										event.currentTarget.style.transform = 'translateY(-1px)';
										event.currentTarget.style.filter = 'brightness(1.05)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.86, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 12px 22px ${hexToRgba(colors.firstVark, 0.22, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
									onMouseLeave={(event) => {
										event.currentTarget.style.transform = 'translateY(0)';
										event.currentTarget.style.filter = 'brightness(1)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.56, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 8px 18px ${hexToRgba(colors.firstVark, 0.16, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
								>
									{t('import.reset')}
								</button>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
								{audioFiles.map((file, idx) => (
									<div key={idx} style={{
										background: colors.chipBg,
										border: `1px solid ${colors.chipBorder}`,
										borderRadius: '10px',
										padding: '8px 12px',
										display: 'flex', alignItems: 'center', justifyContent: 'space-between',
										boxShadow: `0 8px 18px ${hexToRgba(colors.firstBackground, 0.24, colors.firstBackground)}`,
									}}>
										<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
											<FiMic size={16} color={colors.firstVark} />
											<span style={{ color: colors.textPrimary, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{file?.name || t('import.audioFileFallback')}
											</span>
										</div>
										<button
											type="button"
											onClick={() => removeAudio(idx)}
											style={{
												background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer',
												padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
												outline: 'none',
											}}
										>
											<FiX size={16} />
										</button>
									</div>
								))}
							</div>
							<Button variant="primary" color={colors.firstVark} size="md" onClick={confirmAudio} disabled={isExtractingAudio}>
								{t('import.confirmImport', { count: audioFiles.length })}
							</Button>
						</div>
					)}
					<input
						ref={audioInputRef}
						type="file"
						accept="audio/*"
						multiple
						style={{ display: 'none' }}
						onChange={(e) => handleAudioSelected(e.target.files)}
					/>
				</CenteredMenu>
			)}
		</>
	);
};

// ─── ImportFileFeature ────────────────────────────────────────────────────────
const ImportFileFeature = ({
	onFileImport,
	size = 'md',
	allImportButtonProps = {},
	triggerImportButtonProps = {},
	chooseImportButtonProps = {},
	addImportButtonProps = {},
	resetImportButtonProps = {},
	confirmImportButtonProps = {},
}) => {
	const colors = useButtonsImportColors();
	const { t } = useLanguageContext();
	const [open, setOpen] = useState(false);
	const [dragOver, setDragOver] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [isExtractingFiles, setIsExtractingFiles] = useState(false);
	const fileInputRef = useRef(null);

	const handleFileSelected = (filesOrFile) => {
		const nextFiles = toFileArray(filesOrFile);
		if (nextFiles.length === 0) return;
		setSelectedFiles((currentFiles) => [...currentFiles, ...nextFiles]);
	};

	const removeFile = (index) => {
		setSelectedFiles((currentFiles) => currentFiles.filter((_, i) => i !== index));
	};

	const clearAllFiles = () => {
		setSelectedFiles([]);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const confirmFiles = async () => {
		if (selectedFiles.length === 0 || isExtractingFiles) return;

		setIsExtractingFiles(true);
		console.log('[ImportFileFeature] confirmFiles triggered with', selectedFiles.length, 'files');
		try {
			const extracted = await Promise.all(selectedFiles.map(async (file) => {
				if (!file || typeof file !== 'object') return null;
				const name = (file.name || '').toLowerCase();
				const mimeType = (file.type || '').toLowerCase();
				console.log('[ImportFileFeature] Processing file:', name, '| mimeType:', mimeType);
				
				if (name.endsWith('.pdf') || mimeType === 'application/pdf') {
					console.log('[ImportFileFeature] ✅ Detected PDF:', name);
					return {
						type: 'pdf',
						fileName: file?.name || 'PDF',
						mimeType: file?.type || 'application/pdf',
						size: Number(file?.size) || 0,
						lastModified: Number(file?.lastModified) || null,
						file,
					};
				}
				
				// Image detection: check extension and MIME type
				const imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.svg'];
				const isImageByExt = imageExtensions.some(ext => name.endsWith(ext));
				const isImageByMime = mimeType.startsWith('image/');
				
				if (isImageByExt || isImageByMime) {
					console.log('[ImportFileFeature] ✅ Detected IMAGE:', name, '| byExt:', isImageByExt, '| byMime:', isImageByMime);
					return {
						type: 'image',
						fileName: file?.name || 'Image',
						mimeType: file?.type || 'image/unknown',
						size: Number(file?.size) || 0,
						lastModified: Number(file?.lastModified) || null,
						file,
					};
				}
				
				// Doc detection: check extension and MIME type
				const docExtensions = ['.doc', '.docx', '.odt'];
				const isDocByExt = docExtensions.some(ext => name.endsWith(ext));
				const isDocByMime = mimeType.includes('word') || mimeType.includes('document') || 
				                    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
				                    mimeType === 'application/msword' ||
				                    mimeType === 'application/vnd.oasis.opendocument.text';
				
				if (isDocByExt || isDocByMime) {
					console.log('[ImportFileFeature] ✅ Detected DOC:', name, '| byExt:', isDocByExt, '| byMime:', isDocByMime);
					return {
						type: 'doc',
						fileName: file?.name || 'Document',
						mimeType: file?.type || 'application/octet-stream',
						size: Number(file?.size) || 0,
						lastModified: Number(file?.lastModified) || null,
						file,
					};
				}

				console.log('[ImportFileFeature] ❌ Unsupported file type:', name, '| mimeType:', mimeType);
				return {
					type: 'unknown',
					fileName: file?.name || 'File',
					text: 'Unsupported format: only PDF, images or Word documents are accepted.',
					file: null,
				};
			}));

			console.log('[ImportFileFeature] extracted payloads:', extracted.filter(p => p !== null).length, 'non-null items');
			extracted.forEach((payload) => {
				console.log('[ImportFileFeature] Calling onFileImport with payload type:', payload?.type);
				onFileImport?.(payload);
			});

			setSelectedFiles([]);
			setOpen(false);
		} catch (error) {
			console.error('[ImportFileFeature] Error in confirmFiles:', error);
			onFileImport?.({
				type: 'pdf',
				fileName: 'PDF import',
				text: `PDF import error: ${error?.message || 'unknown error'}`,
			});
		} finally {
			setIsExtractingFiles(false);
		}
	};

	const dropZoneStyle = useMemo(() => ({
		border: `2px dashed ${dragOver ? colors.firstVark : colors.inputBorder}`,
		borderRadius: '12px',
		padding: '40px 20px',
		textAlign: 'center',
		color: dragOver ? colors.textPrimary : colors.textSecondary,
		background: dragOver ? colors.accentBgSoft : colors.cardBg,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	}), [colors, dragOver]);

	const sizePreset = importSizePresets[size] || importSizePresets.md;

	return (
		<>
			<StyledActionCard
				icon={<FiFileText size={sizePreset.iconSize} color={colors.firstVark} strokeWidth={1.5} />}
				title={t('import.importFile')}
				desc={t('import.supportedFormats')}
				highlight={false}
				onClick={() => setOpen((v) => !v)}
				style={{ minHeight: sizePreset.minHeight, padding: sizePreset.padding, ...allImportButtonProps.style, ...triggerImportButtonProps.style }}
				{...allImportButtonProps}
				{...triggerImportButtonProps}
			/>

			{open && (
				<CenteredMenu
					title={t('import.importFile')}
					subtitle={t('import.importFileSubtitle')}
					onClose={() => setOpen(false)}
					colors={colors}
				>
					{selectedFiles.length === 0 ? (
						<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<button
								type="button"
								onClick={() => fileInputRef.current?.click()}
								style={getElegantMenuButtonStyle(colors, 'primary')}
								onMouseEnter={(e) => {
									e.currentTarget.style.transform = 'translateY(-2px)';
									e.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.85, colors.firstVark);
									e.currentTarget.style.boxShadow = `0 14px 28px ${hexToRgba(colors.firstVark, 0.26, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = 'translateY(0)';
									e.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.6, colors.firstVark);
									e.currentTarget.style.boxShadow = `0 10px 22px ${hexToRgba(colors.firstVark, 0.2, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
								}}
							>
								<span style={buttonContentStyle}><FiFolder size={16} />{t('import.importFileSubtitle')}</span>
							</button>

							<div
								style={dropZoneStyle}
								onClick={() => fileInputRef.current?.click()}
								onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
								onDragLeave={() => setDragOver(false)}
								onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileSelected(e.dataTransfer?.files); }}
							>
								<div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
									<FiUploadCloud size={32} />
								</div>
								<div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>
									{t('import.dragDropFiles')}
								</div>
								<div style={{ fontSize: '12px', color: colors.textSecondary }}>{t('import.supportedFormats')}</div>
							</div>
						</div>
					) : (
						<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							<div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									style={getCompactAccentButtonStyle(colors, 'primary')}
									onMouseEnter={(event) => {
										event.currentTarget.style.transform = 'translateY(-1px)';
										event.currentTarget.style.filter = 'brightness(1.05)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.86, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 12px 22px ${hexToRgba(colors.firstVark, 0.22, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
									onMouseLeave={(event) => {
										event.currentTarget.style.transform = 'translateY(0)';
										event.currentTarget.style.filter = 'brightness(1)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.56, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 8px 18px ${hexToRgba(colors.firstVark, 0.16, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
								>
									<span style={buttonContentStyle}><FiUploadCloud size={14} />{t('import.addAnotherFile')}</span>
								</button>
								<button
									type="button"
									onClick={clearAllFiles}
									style={getCompactAccentButtonStyle(colors, 'primary')}
									onMouseEnter={(event) => {
										event.currentTarget.style.transform = 'translateY(-1px)';
										event.currentTarget.style.filter = 'brightness(1.05)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.86, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 12px 22px ${hexToRgba(colors.firstVark, 0.22, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
									onMouseLeave={(event) => {
										event.currentTarget.style.transform = 'translateY(0)';
										event.currentTarget.style.filter = 'brightness(1)';
										event.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.56, colors.firstVark);
										event.currentTarget.style.boxShadow = `0 8px 18px ${hexToRgba(colors.firstVark, 0.16, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
									}}
								>
									{t('import.reset')}
								</button>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
								{selectedFiles.map((file, idx) => (
									<div key={idx} style={{
										background: colors.chipBg,
										border: `1px solid ${colors.chipBorder}`,
										borderRadius: '10px',
										padding: '8px 12px',
										display: 'flex', alignItems: 'center', justifyContent: 'space-between',
										boxShadow: `0 8px 18px ${hexToRgba(colors.firstBackground, 0.24, colors.firstBackground)}`,
									}}>
										<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
											<FiFileText size={16} color={colors.firstVark} />
											<span style={{ color: colors.textPrimary, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
											{String(file?.name || 'File')}
											</span>
										</div>
										<button
											type="button"
											onClick={() => removeFile(idx)}
											style={{
												background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer',
												padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
												outline: 'none',
											}}
										>
											<FiX size={16} />
										</button>
									</div>
								))}
							</div>
							<Button variant="primary" color={colors.firstVark} size="md" onClick={confirmFiles} disabled={isExtractingFiles}>
								{t('import.confirmFileImport', { count: selectedFiles.length })}
							</Button>
						</div>
					)}
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,application/pdf,image/*,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
						multiple
						style={{ display: 'none' }}
						onChange={(e) => handleFileSelected(e.target.files)}
					/>
				</CenteredMenu>
			)}
		</>
	);
};

// ─── PastLinkVideoFeature ─────────────────────────────────────────────────────
const PastLinkVideoFeature = ({
	onYoutubeLink,
	size = 'md',
	allImportButtonProps = {},
	triggerImportButtonProps = {},
	validateImportButtonProps = {},
}) => {
	const colors = useButtonsImportColors();
	const { t } = useLanguageContext();
	const [open, setOpen] = useState(false);
	const [url, setUrl] = useState('');
	const [error, setError] = useState('');

	const sizePreset = importSizePresets[size] || importSizePresets.md;

	const handleOpen = () => {
		setOpen(true);
		setError('');
	};

	const handleClose = () => {
		setOpen(false);
		setError('');
		setUrl('');
	};

	const handleChange = (e) => {
		setUrl(e.target.value);
		setError('');
	};

	const handleSubmit = () => {
		const trimmed = url.trim();
		if (!trimmed) {
			setError('Please enter a YouTube URL.');
			return;
		}
		// Optionally: validate YouTube URL format
		onYoutubeLink?.(trimmed);
		setUrl('');
		setOpen(false);
		setError('');
	};

	return (
		<>
			<StyledActionCard
				icon={<FiLink size={sizePreset.iconSize} color={colors.firstVark} strokeWidth={1.5} />}
				title={t('import.videoLink')}
				desc={t('import.videoLinkSubtitle')}
				highlight={false}
				onClick={handleOpen}
				style={{ minHeight: sizePreset.minHeight, padding: sizePreset.padding, ...allImportButtonProps.style, ...triggerImportButtonProps.style }}
				{...allImportButtonProps}
				{...triggerImportButtonProps}
			/>

			{open && (
				<CenteredMenu
					title={t('import.videoLink')}
					subtitle={t('import.videoLinkSubtitle')}
					onClose={handleClose}
					colors={colors}
				>
					<input
						type="url"
						placeholder={t('import.videoPlaceholder')}
						value={url}
						onChange={handleChange}
						onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
						style={getInputBase(colors)}
						onFocus={(e) => {
							e.currentTarget.style.borderColor = colors.inputBorderFocus;
							e.currentTarget.style.boxShadow = `0 0 0 3px ${hexToRgba(colors.firstVark, 0.24, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
						}}
						onBlur={(e) => {
							e.currentTarget.style.borderColor = colors.inputBorder;
							e.currentTarget.style.boxShadow = `inset 0 1px 0 ${colors.panelInset}`;
						}}
						autoFocus
					/>
					{error && (
						<div style={{ color: colors.dangerText, fontSize: '13px', margin: '8px 0', textAlign: 'center' }}>{error}</div>
					)}
					<div style={{ fontSize: '12px', color: colors.subtleText, textAlign: 'center' }}>
						{t('import.videoLinkHint')}
					</div>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<button
							type="button"
							onClick={handleSubmit}
							style={{ ...getElegantMenuButtonStyle(colors, 'primary'), width: 'auto', minWidth: '180px' }}
							onMouseEnter={(e) => {
								e.currentTarget.style.transform = 'translateY(-2px)';
								e.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.85, colors.firstVark);
								e.currentTarget.style.boxShadow = `0 14px 28px ${hexToRgba(colors.firstVark, 0.26, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.transform = 'translateY(0)';
								e.currentTarget.style.borderColor = hexToRgba(colors.firstVark, 0.6, colors.firstVark);
								e.currentTarget.style.boxShadow = `0 10px 22px ${hexToRgba(colors.firstVark, 0.2, colors.firstVark)}, inset 0 1px 0 ${colors.panelInset}`;
							}}
						>
							{t('import.confirmLink')}
						</button>
					</div>
				</CenteredMenu>
			)}
		</>
	);
};

export {
	ImportRecordAudioFeature,
	ImportFileFeature,
	PastLinkVideoFeature,
};
